import { AnnouncementIcon } from "@/assets";
import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { ToastMessage, type } from "@/lib/toastr";
import { cn } from "@/lib/utils";
import { updateBeneficiary } from "@/services/Transactions.service";
import { getUserBank } from "@/services/Users.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { BsBank } from "react-icons/bs";
const ChangeBeneficiary = ({
  id,
  open,
  transactionId,
  size,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  id: string;
  transactionId: string;
  size: string;
}) => {
  const queryClient = useQueryClient();
  const [selectAccount, setSelectAccount] = useState({});
  const [defaultValue, setDefaultValue] = useState("");

  const beneficiaryList = useQuery({
    queryKey: ["beneficiaryList", id],
    queryFn: () => getUserBank(id),
    enabled: !!id,
  });

  const mutation = useMutation({
    mutationFn: async (payload) => await updateBeneficiary(transactionId, payload),
    mutationKey: ["changeBeneficiary"],

    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["transactionDetails"] });
      onOpenChange(false);

      ToastMessage(type.SUCCESS, res?.data?.message);
    },
    onError: (err: any) => {
      onOpenChange(false);
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  useEffect(() => {
    if (beneficiaryList?.data?.data?.data !== undefined) {
      const p = beneficiaryList?.data?.data?.data?.filter(
        (i) => i?.default === true
      );
      setDefaultValue(p[0]?.id);
    }
  }, [beneficiaryList?.data?.data?.data, setDefaultValue]);

  return (
    <CustomModal open={open} onOpenChange={onOpenChange} size="md">
      <div className="flex flex-col gap-8">
        <DialogHeader className="flex flex-row  w-full p-0 items-center gap-4 bg-off-white-1 py-6 px-8 rounded-t-[16px]">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-off-white-3">
            <AnnouncementIcon />
          </div>
          <div className="flex flex-col ">
            <DialogTitle className="text-sm font-normal capitalize text-primary-3">
              Chnage beneficiary
            </DialogTitle>
            <DialogDescription className="text-sm">
              Select a new beneficiary account to confirm payout
            </DialogDescription>
          </div>
        </DialogHeader>
      </div>
      <div className="flex flex-col gap-4 px-8 h-[300px] overflow-auto">
        <div className="h-[104px] bg-[#FFF9EB] p-2 rounded-[12px] gap-2 flex flex-col w-full">
          <h5 className="font-medium text-xs text-[#2A0028]">PLEASE NOTE</h5>
          <ul className="list-disc font-normal text-xs text-[#2A0028] pl-5">
            <li>
              Only the customer’s default bank account will be displayed here
            </li>
            <li>
              If the customer is facing troubles with transactions, kindly ask
              them to change their default account
            </li>
          </ul>
        </div>

        <div className="flex flex-col gap-4">
          {beneficiaryList?.data?.data?.data?.map((item: any, index: number) => (
            <div
              key={index}
              className={cn(
                "h-[90px] rounded-[12px] border-[1px] py-3 px-4 text flex items-center gap-3 justify-between",
                item?.default === false
                  ? "bg-[#FBFBFB] border-[#E3E3E3]"
                  : "bg-[#FFF3FF] border-[#FFCAFD]"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-[#FFE9FF] rounded-full flex justify-center items-center">
                  <BsBank size="24px" color="#2A0028" />
                </div>
                <div>
                  <p>{item?.bankName}</p>
                  <h4>{item?.accountName}</h4>
                  <p>{item?.accountNumber}</p>
                </div>
              </div>
              <div className="inline-flex items-center h-full">
                <label
                  className="relative flex items-center cursor-pointer"
                // for={`${item?.id?.toString()}`}
                >
                  <input
                    name="framework"
                    type="radio"
                    className={cn(
                      "peer h-6 w-6 cursor-pointer appearance-none rounded-full transition-all",
                      defaultValue === item?.id
                        ? "bg-[#3D003B]"
                        : "bg-[#ACACAC]"
                    )}
                    id={`${item?.id?.toString()}`}
                    onChange={() => {
                      setDefaultValue(item?.id);
                      setSelectAccount(item);
                    }}
                  />
                  <span
                    className={cn(
                      "absolute bg-[#F5F5F5] peer-checked:bg-[#FFE4FE] w-3 h-3 rounded-full opacity-100 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2",
                      defaultValue === item?.id
                        ? "bg-[#FFE4FE]"
                        : "bg-[#F5F5F5]"
                    )}
                  ></span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>
      <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
        <DialogClose asChild>
          <Button
            type="button"
            variant="ghost"
            className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
          >
            <ChevronLeft /> Cancel
          </Button>
        </DialogClose>
        <Button
          type="button"
          className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
          onClick={() => mutation.mutate({ bankId: defaultValue })}
          disabled={
            JSON.stringify(selectAccount) === "{}" || mutation?.isPending
          }
        >
          {mutation?.isPending && (
            <Loader2 className="text-white animate-spin" />
          )}
          Re-intiate transaction <ChevronRight />
        </Button>
      </DialogFooter>
    </CustomModal>
  );
};

export default ChangeBeneficiary;
